import { useProposalSelectionPreview } from '@/hooks/proposals/useProposalSelectionPreview';
import { ProposalPricingStatus } from '@/components/public/ProposalPricingStatus';
import { proposalCheckoutEstimateId } from '@/lib/sales/proposal-navigation';
import { ProposalCheckout } from '@/components/public/ProposalCheckout';
import { publicEstimateSearch } from '@/lib/sales/public-estimate-search';
import { useState, useEffect } from 'react';
import { flushSync } from 'react-dom';
import { createFileRoute } from '@tanstack/react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { CheckCircle, Loader2, RotateCcw, XCircle } from 'lucide-react';
import { Button } from '@/components/atoms/button';
import { ProposalApproveModal } from '@/components/public/ProposalApproveModal';
import { ProposalItemizedOptionDocument } from '@/components/public/ProposalItemizedOptionDocument';
import { ProposalOptionTabs } from '@/components/public/ProposalOptionTabs';
import {
  proposalApproveRecap,
  proposalOptionDepositBeforeApproval,
  proposalOptionDepositDue,
  mergeOptionAttachments,
  proposalMembershipOffer,
  proposalOptionDisplayTotal,
} from '@/lib/sales/proposalOptionMembership';
import { singleScopeThemeVars } from '@/lib/quote/singleScopeTheme';
import { Separator } from '@/components/atoms/separator';
import { PublicLayout } from '@/components/public/PublicLayout';
import { CustomerTermsBody } from '@/components/public/CustomerTermsBody';
import { DocumentNotFound } from '@/components/public/DocumentNotFound';
import { PoweredByNotifi } from '@/components/public/PoweredByNotifi';
import { ProposalSingleScopeContent } from '@/components/public/ProposalSingleScopeContent';
import { isSingleScopeLayout } from '@/lib/quote/singleScopeTheme';
import type { Proposal, ProposalOption, ProposalStatus } from '@/types/proposal';
import {
  proposalOptionDefaultOfferKeys,
  proposalOptionSelectionChanges,
} from '@/lib/sales/proposalOptionBreakdown';
import { resolveCustomerTerms } from '@/lib/terms/clientTerms';
import { formatDateTimeInTimeZone } from '@/lib/date-values';
import {
  registerSignedProposalPdf,
  withSignedRecordDeadline,
} from '@/lib/proposals/registerSignedProposalPdf';

export const Route = createFileRoute('/(public)/proposal/$id')({
  validateSearch: publicEstimateSearch,
  component: ProposalPage,
});

// ─── Helpers ──────────────────────────────────────────────────────────────────

const OPS_URL = import.meta.env.VITE_OPERATIONS_URL ?? '';

/**
 * The SPA and the API deploy separately, so a customer can be sitting on a tab
 * that predates a change to the request contract (the acceptance field being
 * the case in point). Their next click 400s through no fault of theirs, and
 * "please try again" is the one instruction that cannot work — the stale bundle
 * will send the same body. Tell them to reload instead.
 */
export const STALE_PAGE_MESSAGE = 'This page is out of date. Please refresh and try again.';
/** A 400 the customer CAN fix — the server rejected the name or the drawn signature. */
export const SIGNATURE_PROBLEM_MESSAGE = 'Please check your name and signature, then try again.';
const REQUIRED_PLAN_MESSAGE =
  'A payment plan is required before approval. Please contact the office to confirm it.';

/**
 * What a 400 from the selection means. Keyed on what THIS page sent, not on
 * the server's wording: a 400 about a signature this page provided is
 * something the customer can correct (bad image, missing name, too large).
 * Any other 400 — the missing acceptance, or "a signature is required" from a
 * page that never rendered the pad — is a tab loaded before the current
 * contract shipped, and only a refresh fixes that.
 */
function selectionErrorMessage(status: number, body: unknown, signatureSent: boolean): string {
  if (
    typeof body === 'object' &&
    body !== null &&
    'code' in body &&
    body.code === 'SCHEDULE_REQUIRED'
  )
    return REQUIRED_PLAN_MESSAGE;
  if (status === 409)
    return 'This proposal has changed. Please refresh and review the total before approving.';
  if (status !== 400) return 'Failed to select option';
  const text = JSON.stringify(body ?? '').toLowerCase();
  if (signatureSent && text.includes('signature')) return SIGNATURE_PROBLEM_MESSAGE;
  return STALE_PAGE_MESSAGE;
}

/** What the customer signs with — a typed name plus the pad's PNG data URL. */
export interface ProposalAcceptanceSignature {
  name: string;
  image: string;
}

/** Everything a Select click can carry beyond the option index. */
export interface ProposalSelectionInput {
  expectedTotal?: number;
  acceptMembershipOffer?: boolean;
  membershipBillingOptionId?: string | null;
  signature?: ProposalAcceptanceSignature | null;
  /** The customer's optional add-on picks for the selected option — each entry
   *  flips one optional line's `isSelected`; the server re-prices from them. */
  lineItems?: Array<{ lineItemId: string; lineKey?: string; isSelected: boolean }>;
  /**
   * The option's deposit is required before approval, so choose it
   * (no terms, no signature — those follow the
   * deposit in the checkout) and take the deposit first.
   */
  depositFirst?: boolean;
  /** Review the configured payment plan in checkout before consent or signature. */
  checkoutFirst?: boolean;
  /**
   * The option carries a deposit (Approve and pay): once the acceptance lands,
   * the checkout opens the deposit payment.
   */
  payDeposit?: boolean;
}

function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(amount);
}

async function fetchProposal(id: string): Promise<Proposal> {
  const res = await fetch(`${OPS_URL}/proposals/public/${id}`, {
    headers: { 'Content-Type': 'application/json' },
  });
  if (!res.ok) throw new Error('Proposal not found');
  return res.json();
}

/** Exported for tests — the request contract is the thing under test. */
// eslint-disable-next-line react-refresh/only-export-components
export async function postSelectOption(
  id: string,
  _tenantId: string,
  optionIndex: number,
  acceptMembershipOffer = false,
  membershipBillingOptionId?: string | null,
  signature?: ProposalAcceptanceSignature | null,
  lineItems?: ProposalSelectionInput['lineItems'],
  expectedTotal?: number,
): Promise<Proposal & { redirectUrl?: string | null }> {
  const res = await fetch(`${OPS_URL}/proposals/public/${id}/select-option`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    // The server refuses the selection without this — the checkbox is the UX,
    // `terms_accepted` is what actually gates and gets recorded. Only ever sent
    // from handleSelect, which is behind the tick.
    body: JSON.stringify({
      option_index: optionIndex,
      terms_accepted: true,
      ...(acceptMembershipOffer && { acceptMembershipOffer: true }),
      ...(acceptMembershipOffer && membershipBillingOptionId ? { membershipBillingOptionId } : {}),
      // The customer's optional add-on picks — the server re-prices the option
      // from them before it materializes the estimate.
      ...(lineItems && lineItems.length ? { lineItems } : {}),
      ...(expectedTotal != null ? { expectedTotal } : {}),
      // The server refuses a signature-required proposal without these, and
      // records them on any proposal when offered.
      ...(signature
        ? {
            customer_signature_name: signature.name,
            customer_signature_image: signature.image,
          }
        : {}),
    }),
  });
  if (!res.ok) {
    // The page and the API deploy separately. A tab opened before the acceptance
    // gate shipped posts the old body and gets a 400 it can do nothing about —
    // so say "refresh" rather than "something went wrong", which invites the
    // customer to retry the same stale request. A refused signature is the one
    // 400 the customer can act on, and gets told so.
    const body = await res.json().catch(() => null);
    throw new Error(selectionErrorMessage(res.status, body, signature != null));
  }
  return res.json();
}

/**
 * The deposit-first path: the option is chosen as configured (membership and
 * add-on picks), the estimate is born SENT, and the response carries the
 * estimate path where the customer pays the deposit and approves.
 */
// eslint-disable-next-line react-refresh/only-export-components
export async function postChooseOptionForDeposit(
  id: string,
  optionIndex: number,
  acceptMembershipOffer: boolean,
  membershipBillingOptionId?: string | null,
  lineItems?: ProposalSelectionInput['lineItems'],
  expectedTotal?: number,
): Promise<Proposal & { redirectUrl?: string | null }> {
  const res = await fetch(`${OPS_URL}/proposals/public/${id}/choose-option-for-deposit`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      option_index: optionIndex,
      ...(acceptMembershipOffer && { acceptMembershipOffer: true }),
      ...(acceptMembershipOffer && membershipBillingOptionId ? { membershipBillingOptionId } : {}),
      ...(lineItems && lineItems.length ? { lineItems } : {}),
      ...(expectedTotal != null ? { expectedTotal } : {}),
    }),
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => null)) as {
      message?: unknown;
      code?: string;
    } | null;
    if (body?.code === 'SCHEDULE_REQUIRED') throw new Error(REQUIRED_PLAN_MESSAGE);
    const message = Array.isArray(body?.message) ? body?.message[0] : body?.message;
    throw new Error(
      typeof message === 'string' && message
        ? message
        : 'We could not continue to the deposit. Please refresh and try again.',
    );
  }
  return res.json();
}

async function declineProposal(id: string, reason: string): Promise<Proposal> {
  const res = await fetch(`${OPS_URL}/proposals/public/${id}/decline`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ reason: reason.trim() || undefined }),
  });
  if (!res.ok) throw new Error('Failed to decline proposal');
  return res.json();
}

const SELECTABLE_STATUSES: ProposalStatus[] = ['SENT', 'VIEWED', 'FOLLOW_UP'];

// ─── Page Component ───────────────────────────────────────────────────────────

function ProposalPage() {
  const { id } = Route.useParams();
  const queryClient = useQueryClient();
  const proposalQueryKey = ['public', 'proposal', id] as const;
  const [proposal, setProposal] = useState<Proposal | null>(null);
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [showDeclineForm, setShowDeclineForm] = useState(false);
  const [declineReason, setDeclineReason] = useState('');
  // Options with required deposits are chosen without accepting; checkout takes
  // payment first. All other selections require consent and signature here.
  const [termsAccepted, setTermsAccepted] = useState(false);

  const {
    data: fetchedProposal,
    isLoading,
    isError,
    refetch,
  } = useQuery<Proposal>({
    queryKey: proposalQueryKey,
    queryFn: () => fetchProposal(id),
    retry: false,
    // Global queries remain fresh for one minute. A customer returning from the
    // generated estimate must not see the pre-selection proposal during that
    // window.
    refetchOnMount: 'always',
    refetchOnWindowFocus: 'always',
  });

  useEffect(() => {
    if (!fetchedProposal) return;
    setProposal(fetchedProposal);
    setSelectedIndex(fetchedProposal.selectedOptionIndex ?? null);
  }, [fetchedProposal]);

  useEffect(() => {
    // Safari and Chromium can restore this page from the back-forward cache
    // without remounting React. `pageshow` covers that path; focus refetching
    // alone is not guaranteed to run for a BFCache restore.
    const handlePageShow = () => {
      void refetch();
    };
    window.addEventListener('pageshow', handlePageShow);
    return () => window.removeEventListener('pageshow', handlePageShow);
  }, [refetch]);

  const [changing, setChanging] = useState(false);
  // Open checkout payment after choosing a required-deposit option, or after
  // accepting an option with an optional deposit.
  const [payDepositOnLoad, setPayDepositOnLoad] = useState(false);
  // Query results arrive one render before the synchronization effect above.
  // Use the fetched value immediately so a successful initial request never
  // flashes the not-found state.
  const currentProposal = proposal ?? fetchedProposal;

  const canSelect =
    currentProposal != null &&
    SELECTABLE_STATUSES.includes(currentProposal.status) &&
    (selectedIndex == null || changing);

  const handleSelect = async (optionIndex: number, selection?: ProposalSelectionInput) => {
    if (!currentProposal || submitting) return;
    if (selection?.depositFirst || selection?.checkoutFirst) {
      setSubmitting(true);
      try {
        const updated = await postChooseOptionForDeposit(
          id,
          optionIndex,
          selection.acceptMembershipOffer === true,
          selection.membershipBillingOptionId,
          selection.lineItems,
          selection.expectedTotal,
        );
        queryClient.setQueryData(proposalQueryKey, updated);
        setProposal(updated);
        setSelectedIndex(updated.selectedOptionIndex ?? optionIndex);
        setChanging(false);
        setTermsAccepted(false);
        setPayDepositOnLoad(selection.depositFirst === true);
      } catch (error) {
        toast.error(
          error instanceof Error && error.message === REQUIRED_PLAN_MESSAGE
            ? error.message
            : 'We could not continue to approval. Please refresh and try again.',
        );
      } finally {
        setSubmitting(false);
      }
      return;
    }
    if (!termsAccepted) {
      toast.error('Please accept the terms to continue');
      return;
    }
    // The CTA is disabled until the pad and name are filled; this is the
    // backstop for a stale render, mirroring the server's own refusal.
    if (currentProposal.signatureRequired && !selection?.signature) {
      toast.error('Please type your name and draw your signature to continue');
      return;
    }
    setSubmitting(true);
    try {
      const updated = await postSelectOption(
        id,
        currentProposal.tenantId,
        optionIndex,
        selection?.acceptMembershipOffer === true,
        selection?.membershipBillingOptionId,
        selection?.signature ?? null,
        selection?.lineItems,
        selection?.expectedTotal,
      );
      queryClient.setQueryData(proposalQueryKey, updated);

      // Commit the accepted proposal before background refreshes or PDF work.
      flushSync(() => {
        setProposal(updated);
        setSelectedIndex(updated.selectedOptionIndex ?? optionIndex);
        setChanging(false);
        setTermsAccepted(false);
        setSubmitting(false);
      });

      // Save the document of record. Acceptance and the signature are already
      // durable, so a slow PDF upload must not invite a duplicate approval.
      if (selection?.signature) {
        const savingToast = toast.loading('Saving your signed copy…');
        await withSignedRecordDeadline(
          registerSignedProposalPdf({
            opsBaseUrl: OPS_URL,
            id,
            reason: 'signature_collected',
            content: {
              proposal: { ...updated, signatureDataUrl: selection.signature.image },
              company: {
                name: updated.company?.name ?? 'Your provider',
                logoUrl: updated.company?.logoUrl ?? null,
                // Single-scope signed PDFs render the inline logo (a remote
                // logoUrl CORS-fails), so carry it or the tenant logo drops to
                // initials on the signed copy.
                logoDataUri: updated.company?.logoDataUri ?? null,
              },
            },
          }),
        );
        toast.dismiss(savingToast);
      }

      toast.success('Option selected! Thank you.');
      // Only now: a Checkout redirect must not cut the signed copy short.
      if (selection?.payDeposit) setPayDepositOnLoad(true);
    } catch (err) {
      // Only our own message is surfaced — server text is never shown to a
      // customer on a public page.
      toast.error(
        err instanceof Error &&
          (err.message === STALE_PAGE_MESSAGE ||
            err.message === SIGNATURE_PROBLEM_MESSAGE ||
            err.message === REQUIRED_PLAN_MESSAGE)
          ? err.message
          : 'Something went wrong. Please try again.',
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleDecline = async () => {
    if (!currentProposal || submitting) return;
    setSubmitting(true);
    try {
      const updated = await declineProposal(id, declineReason);
      queryClient.setQueryData(proposalQueryKey, updated);
      setProposal(updated);
      setShowDeclineForm(false);
      setDeclineReason('');
      toast.success('Proposal declined');
    } catch {
      toast.error('Failed to decline. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  // ── Loading ─────────────────────────────────────────────────────────────────

  if (isLoading) {
    return (
      <PublicLayout maxWidth="lg">
        <div className="flex justify-center py-20">
          <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
        </div>
      </PublicLayout>
    );
  }

  if ((isError && !currentProposal) || !currentProposal) {
    return <DocumentNotFound message="Proposal not found" />;
  }

  const maxWidth = 'xl';

  // ── Success banner when option already selected ─────────────────────────────

  const showSuccessBanner =
    selectedIndex != null && currentProposal.selectedOptionIndex != null && !changing;

  const canChange = SELECTABLE_STATUSES.includes(currentProposal.status) && !changing;

  const checkout = proposalCheckoutEstimateId(currentProposal) ? (
    <ProposalCheckout
      proposal={currentProposal}
      openDepositOnLoad={payDepositOnLoad}
      onAccepted={(accepted) => {
        setProposal(accepted);
        queryClient.setQueryData(proposalQueryKey, accepted);
      }}
    />
  ) : null;

  // Single-scope design → the proposal presents as single-scope (full-bleed rail
  // + per-option scope cards), mirroring the single-scope estimate. Rendered
  // OUTSIDE PublicLayout so the rail is full-height, like quote.$id.tsx.
  if (isSingleScopeLayout(currentProposal.resolvedDocLayout)) {
    return (
      <ProposalSingleScopeContent
        proposal={currentProposal}
        checkout={checkout}
        selectedIndex={changing ? null : selectedIndex}
        canSelect={canSelect}
        submitting={submitting}
        onSelect={handleSelect}
        onTermsAcceptedChange={setTermsAccepted}
        showSuccessBanner={showSuccessBanner}
        canChange={canChange}
        onChangeSelection={() => setChanging(true)}
        showDeclineForm={showDeclineForm}
        declineReason={declineReason}
        onDeclineReasonChange={setDeclineReason}
        onToggleDeclineForm={setShowDeclineForm}
        onDecline={handleDecline}
      />
    );
  }

  return (
    <PublicLayout maxWidth={maxWidth}>
      <ProposalContent
        proposal={currentProposal}
        checkout={checkout}
        selectedIndex={changing ? null : selectedIndex}
        canSelect={canSelect}
        submitting={submitting}
        showSuccessBanner={showSuccessBanner}
        canChange={canChange}
        onChangeSelection={() => setChanging(true)}
        showDeclineForm={showDeclineForm}
        declineReason={declineReason}
        onDeclineReasonChange={setDeclineReason}
        onToggleDeclineForm={setShowDeclineForm}
        onDecline={handleDecline}
        onSelect={handleSelect}
        termsAccepted={termsAccepted}
        onTermsAcceptedChange={setTermsAccepted}
      />
    </PublicLayout>
  );
}

// ─── Proposal Content ─────────────────────────────────────────────────────────

interface ProposalContentProps {
  checkout?: React.ReactNode;
  proposal: Proposal;
  selectedIndex: number | null;
  canSelect: boolean;
  submitting: boolean;
  showSuccessBanner: boolean;
  canChange: boolean;
  onChangeSelection: () => void;
  showDeclineForm: boolean;
  declineReason: string;
  onDeclineReasonChange: (value: string) => void;
  onToggleDeclineForm: (open: boolean) => void;
  onDecline: () => void;
  onSelect: (index: number, selection?: ProposalSelectionInput) => void;
  termsAccepted: boolean;
  onTermsAcceptedChange: (accepted: boolean) => void;
  /**
   * Whether to offer declining. On by default — the customer's own copy always
   * has it. Presentation mode turns it off: a technician's device is not where
   * a refusal is recorded, and a control that renders without a handler behind
   * it is worse than one that isn't there.
   */
  canDecline?: boolean;
}

/**
 * Exported for tests: the TanStack router plugin rewrites route components in
 * the vitest pipeline, so the page itself can't be rendered directly (same
 * reason quote.$id.tsx exports EstimateView).
 */
export function ProposalContent({
  checkout,
  proposal,
  selectedIndex,
  canSelect,
  submitting,
  showSuccessBanner,
  canChange,
  onChangeSelection,
  showDeclineForm,
  declineReason,
  onDeclineReasonChange,
  onToggleDeclineForm,
  onDecline,
  onSelect,
  onTermsAcceptedChange,
  canDecline = true,
}: ProposalContentProps) {
  const [pendingOptionIndex, setPendingOptionIndex] = useState<number | null>(null);
  const [viewedIndex, setViewedIndex] = useState(
    () =>
      selectedIndex ??
      Math.max(
        0,
        proposal.options.findIndex((option) => option.recommended),
      ),
  );
  // A successful acceptance makes the document non-selectable. Keep the modal
  // (including its signature) intact while sending and on a failed attempt.
  useEffect(() => {
    if (!canSelect) setPendingOptionIndex(null);
  }, [canSelect]);
  const [membershipSelections, setMembershipSelections] = useState<Record<number, boolean>>({});
  const [membershipBillingSelections, setMembershipBillingSelections] = useState<
    Record<number, string>
  >({});
  const [optionalSelections, setOptionalSelections] = useState<Record<number, Set<string>>>({});
  const selectedKeysFor = (option: ProposalOption, index: number) =>
    optionalSelections[index] ?? proposalOptionDefaultOfferKeys(option);
  const toggleOptional = (index: number, option: ProposalOption, key: string) =>
    setOptionalSelections((current) => {
      const next = new Set(current[index] ?? proposalOptionDefaultOfferKeys(option));
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return { ...current, [index]: next };
    });
  const pricingPreview = useProposalSelectionPreview({
    proposal,
    enabled: canSelect,
    optionalSelections,
    membershipSelections,
    membershipBillingSelections,
  });
  const optionFor = (index: number) => pricingPreview.optionFor(index)!;
  const signatureRequired = proposal.signatureRequired === true;
  // Terms reach a customer here exactly as they do on the public quote, so they
  // get the same treatment: rendered as rich text when the tenant authored them
  // in the editor (a T&C template is always editor HTML), and run through the
  // NOT-277 blank-placeholder stripper first so no unfilled "[__]%" clause is
  // shown to the person being asked to agree to it.
  const terms = resolveCustomerTerms(
    typeof proposal.terms === 'string'
      ? proposal.terms
      : (proposal.metadata?.terms as string | undefined),
  );
  // Approve and pay: the option being signed carries a deposit, so the
  // checkout opens its payment once the acceptance lands.
  const pendingPaysDeposit = (() => {
    if (pendingOptionIndex == null) return false;
    if (!proposal.options[pendingOptionIndex]) return false;
    const chosen = optionFor(pendingOptionIndex);
    const billingId =
      membershipBillingSelections[pendingOptionIndex] ??
      proposalMembershipOffer(chosen)?.billingOptionId ??
      null;
    return (
      proposalOptionDepositDue(
        chosen,
        membershipSelections[pendingOptionIndex] === true,
        billingId,
      ) > 0
    );
  })();

  const activeIndex = Math.max(0, Math.min(viewedIndex, proposal.options.length - 1));
  const active = proposal.options[activeIndex] ? optionFor(activeIndex) : null;
  const activeBillingId = active
    ? (membershipBillingSelections[activeIndex] ??
      proposalMembershipOffer(active)?.billingOptionId ??
      null)
    : null;
  const activeMemberSelected = canSelect && membershipSelections[activeIndex] === true;
  const activeOffer = active && canSelect ? proposalMembershipOffer(active, activeBillingId) : null;
  const activeTotal = active
    ? proposalOptionDisplayTotal(active, activeMemberSelected, activeBillingId)
    : 0;
  const activeDeposit = active
    ? proposalOptionDepositDue(active, activeMemberSelected, activeBillingId)
    : 0;
  const chooseOption = (optionIndex: number) => {
    const raw = proposal.options[optionIndex];
    if (!raw || !pricingPreview.stateFor(optionIndex).ready) return;
    // The option as the customer configured it — the same add-on
    // delta and membership choice the approve modal would send.
    const keys = selectedKeysFor(raw, optionIndex);
    const lineItems = proposalOptionSelectionChanges(raw, keys);
    const chosen = optionFor(optionIndex);
    const memberSel = membershipSelections[optionIndex] === true;
    const offer = proposalMembershipOffer(chosen);
    const billingId = membershipBillingSelections[optionIndex] ?? offer?.billingOptionId ?? null;
    // Required deposits and configured plans are reviewed in checkout before signing.
    const depositFirst = proposalOptionDepositBeforeApproval(chosen, memberSel, billingId) > 0;
    if (depositFirst || raw.paymentScheduleConfigured === true) {
      onSelect(optionIndex, {
        ...(depositFirst ? { depositFirst: true } : { checkoutFirst: true }),
        ...(proposal.selectionPreviewSupported
          ? { expectedTotal: proposalOptionDisplayTotal(chosen, memberSel, billingId) }
          : {}),
        ...(lineItems.length ? { lineItems } : {}),
        ...(offer
          ? {
              acceptMembershipOffer: memberSel,
              ...(memberSel && billingId ? { membershipBillingOptionId: billingId } : {}),
            }
          : {}),
      });
      return;
    }
    setPendingOptionIndex(optionIndex);
  };

  return (
    <div className="space-y-6">
      {proposal.status === 'DECLINED' && (
        <div className="flex h-full flex-col rounded-lg border bg-white p-6 shadow-sm">
          <div className="flex items-start gap-3">
            <XCircle className="mt-0.5 h-6 w-6 shrink-0 text-red-500" />
            <div>
              <p className="text-sm font-semibold text-gray-900">Proposal Declined</p>
              <p className="mt-1 text-sm text-gray-500">
                This proposal was declined and is no longer awaiting a selection.
              </p>
            </div>
          </div>
          {proposal.declineReason && (
            <div className="mt-4 rounded-md border border-red-100 bg-red-50 px-3 py-2">
              <p className="text-xs font-medium uppercase tracking-wide text-red-700">
                Decline Reason
              </p>
              <p className="mt-1 whitespace-pre-wrap text-sm text-red-900">
                {proposal.declineReason}
              </p>
            </div>
          )}
        </div>
      )}

      {showSuccessBanner && (
        <div className="flex h-full flex-col rounded-lg border bg-white p-6 shadow-sm">
          <div className="flex items-start gap-3">
            <CheckCircle className="mt-0.5 h-6 w-6 shrink-0 text-green-500" />
            <div>
              <p className="text-sm font-semibold text-gray-900">
                {proposal.customerAcceptedAt ? 'Proposal approved' : 'Option selected'}
              </p>
              <p className="mt-1 text-sm text-gray-500">
                You selected{' '}
                <span className="font-medium text-gray-900">
                  {selectedIndex != null ? proposal.options[selectedIndex]?.label : null}
                </span>
                .{' '}
                {proposal.customerAcceptedAt
                  ? 'Your approval has been recorded.'
                  : checkout
                    ? 'Complete payment and approval below.'
                    : "We'll be in touch shortly."}
              </p>
              {proposal.customerSignatureName ? (
                <div className="mt-3 flex items-center gap-3 rounded-md border bg-gray-50 p-3">
                  {proposal.customerSignatureUrl ? (
                    <img
                      src={proposal.customerSignatureUrl}
                      alt={`Signature of ${proposal.customerSignatureName}`}
                      className="h-10 w-auto max-w-[160px] object-contain"
                    />
                  ) : null}
                  <p className="text-xs text-gray-500">
                    Signed by{' '}
                    <span className="font-medium text-gray-900">
                      {proposal.customerSignatureName}
                    </span>
                    {proposal.customerSignatureAt
                      ? ` on ${formatDateTimeInTimeZone(proposal.customerSignatureAt, proposal.timezone, { dateStyle: 'medium', timeStyle: 'short' })}`
                      : ''}
                  </p>
                </div>
              ) : null}
            </div>
          </div>
          {canChange && (
            <button
              onClick={onChangeSelection}
              className="mt-auto inline-flex items-center gap-1.5 text-sm text-gray-400 transition-colors hover:text-gray-600"
            >
              <RotateCcw className="h-3.5 w-3.5" />
              Change my selection
            </button>
          )}
        </div>
      )}

      {checkout}
      {!pricingPreview.stateFor(activeIndex).ready ? (
        <ProposalPricingStatus
          error={pricingPreview.stateFor(activeIndex).error}
          onRetry={pricingPreview.retry}
        />
      ) : null}

      {active ? (
        <ProposalOptionTabs
          options={proposal.options.map((option, index) => ({
            label: option.label,
            color: option.color,
            recommended: option.recommended,
            total: proposalOptionDisplayTotal(
              optionFor(index),
              canSelect && membershipSelections[index] === true,
              membershipBillingSelections[index] ??
                proposalMembershipOffer(option)?.billingOptionId,
            ),
          }))}
          activeIndex={activeIndex}
          onSelect={setViewedIndex}
        >
          <ProposalItemizedOptionDocument
            proposal={proposal}
            option={active}
            optionLabel={active.label || `Option ${activeIndex + 1}`}
            offer={activeOffer}
            membershipSelected={activeMemberSelected}
            onMembershipSelectedChange={(selected) =>
              setMembershipSelections((current) => ({ ...current, [activeIndex]: selected }))
            }
            billingOptionId={activeBillingId}
            onMembershipBillingChange={(id) =>
              setMembershipBillingSelections((current) => ({ ...current, [activeIndex]: id }))
            }
            displayTotal={activeTotal}
            depositDue={activeDeposit}
            canToggleOptionals={canSelect}
            optionalsDisabled={submitting}
            showFinancing={canSelect}
            onToggleOptional={(key) =>
              toggleOptional(activeIndex, proposal.options[activeIndex]!, key)
            }
            customer={proposal.customer}
            notes={proposal.notes}
            attachments={mergeOptionAttachments(proposal.attachments, active.attachments)}
            terms={terms}
          />
          {canSelect ? (
            <Button
              className="mt-6 w-full"
              disabled={submitting || !pricingPreview.stateFor(activeIndex).ready}
              onClick={() => chooseOption(activeIndex)}
            >
              {submitting ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <CheckCircle className="mr-2 h-4 w-4" />
              )}
              {active.depositRequired === true && activeDeposit > 0
                ? `Pay ${formatCurrency(activeDeposit)} deposit to approve`
                : proposal.options[activeIndex]?.paymentScheduleConfigured === true
                  ? 'Review payment plan'
                  : activeDeposit > 0
                    ? 'Approve and pay'
                    : 'Select This Option'}
            </Button>
          ) : null}
        </ProposalOptionTabs>
      ) : (
        <p className="py-6 text-center text-sm text-gray-500">This proposal has no options.</p>
      )}

      {canDecline && SELECTABLE_STATUSES.includes(proposal.status) && (
        <div className="flex h-full flex-col rounded-lg border bg-white p-6 shadow-sm">
          {showDeclineForm ? (
            <div className="flex flex-1 flex-col space-y-3">
              <label htmlFor="proposal-decline-reason" className="block text-sm font-medium">
                Reason for declining (optional)
              </label>
              <textarea
                id="proposal-decline-reason"
                className="min-h-[120px] w-full flex-1 rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-black focus:outline-none"
                rows={4}
                placeholder="Let us know why you're declining..."
                value={declineReason}
                onChange={(e) => onDeclineReasonChange(e.target.value)}
                maxLength={2000}
              />
              <div className="mt-auto flex gap-2">
                <Button
                  variant="outline"
                  className="flex-1 border-red-200 text-red-700 hover:bg-red-50 hover:text-red-800"
                  onClick={() => {
                    onToggleDeclineForm(false);
                    onDeclineReasonChange('');
                  }}
                  disabled={submitting}
                >
                  Cancel
                </Button>
                <Button
                  className="flex-1 bg-red-600 text-white hover:bg-red-700"
                  onClick={onDecline}
                  disabled={submitting}
                >
                  {submitting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                  Confirm
                </Button>
              </div>
            </div>
          ) : (
            <>
              <p className="text-sm font-semibold text-gray-900">Need something different?</p>
              <p className="mt-1 text-sm text-gray-500">
                If this proposal does not work for you, you can decline it here.
              </p>
              <Button
                variant="outline"
                className="mt-4 w-full border-red-200 text-red-700 hover:bg-red-50 hover:text-red-800"
                onClick={() => onToggleDeclineForm(true)}
                disabled={submitting}
              >
                Decline Proposal
              </Button>
            </>
          )}
        </div>
      )}

      {canSelect && pendingOptionIndex != null && proposal.options[pendingOptionIndex] ? (
        <ProposalApproveModal
          open
          onOpenChange={(open) => {
            if (!open && !submitting) {
              onTermsAcceptedChange(false);
              setPendingOptionIndex(null);
            }
          }}
          proposalNumber={proposal.proposalNumber}
          companyName={proposal.company?.name}
          customerName={proposal.customer?.name}
          signatureRequired={signatureRequired}
          terms={terms}
          recap={proposalApproveRecap(
            optionFor(pendingOptionIndex),
            membershipSelections[pendingOptionIndex] === true,
            membershipBillingSelections[pendingOptionIndex] ?? null,
            proposal.options[pendingOptionIndex]!.label,
          )}
          themeVars={singleScopeThemeVars({ accentColor: '#0A0A0A' })}
          submitting={submitting || !pricingPreview.stateFor(pendingOptionIndex).ready}
          onAcceptanceChange={onTermsAcceptedChange}
          submitLabel={pendingPaysDeposit ? 'Approve and pay' : undefined}
          onApprove={(signature) => {
            const optionIndex = pendingOptionIndex;
            if (!pricingPreview.stateFor(optionIndex).ready) return;
            const raw = proposal.options[optionIndex]!;
            const keys = selectedKeysFor(raw, optionIndex);
            // Only changed rows belong in the request: even an unchanged,
            // non-empty selection makes the server reprice a frozen proposal.
            const lineItems = proposalOptionSelectionChanges(raw, keys);
            const offer = proposalMembershipOffer(optionFor(optionIndex));
            const billingId = membershipBillingSelections[optionIndex] ?? offer?.billingOptionId;
            onSelect(optionIndex, {
              signature,
              ...(proposal.selectionPreviewSupported
                ? {
                    expectedTotal: proposalOptionDisplayTotal(
                      optionFor(optionIndex),
                      membershipSelections[optionIndex] === true,
                      billingId,
                    ),
                  }
                : {}),
              ...(lineItems.length ? { lineItems } : {}),
              ...(offer
                ? {
                    acceptMembershipOffer: membershipSelections[optionIndex] === true,
                    ...(billingId ? { membershipBillingOptionId: billingId } : {}),
                  }
                : {}),
              ...(pendingPaysDeposit ? { payDeposit: true } : {}),
            });
          }}
        />
      ) : null}

      {!active && terms.hasTerms ? (
        <section id="proposal-terms" className="rounded-lg border bg-white p-6 shadow-sm">
          <h3 className="mb-2 text-sm font-semibold text-gray-700">Terms &amp; Conditions</h3>
          <CustomerTermsBody terms={terms} className="text-sm text-gray-600" />
        </section>
      ) : null}

      {/* Footer */}
      <div className="py-6 text-center">
        <Separator className="mb-6" />
        <PoweredByNotifi />
      </div>
    </div>
  );
}
